import { Injectable, Output, EventEmitter } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { throwError, Observable, fromEvent } from 'rxjs';
import { map } from 'rxjs/operators';
import { SNItemFactory } from '../models/snitemfactory';
import { SNItem, SNItemAccess } from '../models/snitem';

export enum ODBActivityType {
  create,
  update,
  delete,
  select
}

export enum ODBActivityStatus {
  starting,
  started,
  ending,
  ended,
  error,
  warning
}

export class ODBActivity {
  constructor(
    public type: ODBActivityType,
    public status: ODBActivityStatus,
    public error: boolean,
    public message: string,
  ) {}
}

export enum ODBConnectionStatus {
  online = 'online',
  offline = 'offline'
}

@Injectable({
  providedIn: 'root'
})
export class ODBService {

  private static instance: ODBService = null;

  onlineStatus: Observable<Event>;
  offlineStatus: Observable<Event>;
  public status = ODBConnectionStatus.online;

  private url = '';
  public callurl = '';
  private sharedkey = '';
  private bearer = '';

  private authenticated = false;

  // Cache params
  private deep$ = true;
  private deeplevels$ = 6;
  private deepItems$ = [];

  // Item cache
  private itemsingleton: Map<string, SNItem> = new Map();
  private itemsById: Map<number, SNItem> = new Map();
  private itemsByKey: Map<string, SNItem[]> = new Map();

  @Output() activity: EventEmitter<ODBActivity> = new EventEmitter();
  @Output() itemStored: EventEmitter<SNItem> = new EventEmitter();
  @Output() connectionStatus: EventEmitter<ODBConnectionStatus> = new EventEmitter();

  constructor(private http: HttpClient, private factory: SNItemFactory) {
    this.sharedkey = environment.odbsharedkey;
    this.url = window.location.protocol + '//' + window.location.host;
    this.callurl = this.url + '/api/v1';

    this.onlineStatus = fromEvent(window, 'online');
    this.offlineStatus = fromEvent(window, 'offline');
    this.status = (navigator.onLine) ? ODBConnectionStatus.online : ODBConnectionStatus.offline;

    this.onlineStatus.subscribe(event => {
      this.connectionStatus.emit(ODBConnectionStatus.online);
      console.log('Online event', event);
    });
    this.offlineStatus.subscribe(event => {
      this.connectionStatus.emit(ODBConnectionStatus.offline);
      console.log('Offline event', event);
    });

    ODBService.instance = this;
  }

  public static getInstance(): ODBService {
    return ODBService.instance;
  }

  auth() {
    // Try to authenticate with sharedkey and/or user/password
    return false;
  }

  set deep(flag) {
    this.deep$ = flag;
  }

  set deepLevel(flag) {
    this.deeplevels$ = flag;
  }

  set deepItems(items) {
    this.deepItems$ = items;
  }

  private deepParams(itemkey) {
    if (this.deepItems$.indexOf(itemkey) !== -1) {
      const queryparams: [string, string][] = [['deep', this.deep$.toString()], ['deeplevels', this.deeplevels$.toString()]];
      return queryparams;
    }
    return [];
  }

  set connBearer(bearer: string) {
    this.bearer = bearer;
  }

  createItem(item): Observable<SNItem> {
    const body = {
      item
    };
    this.activity.emit(new ODBActivity(
      ODBActivityType.create,
      ODBActivityStatus.starting,
      false,
      ''
    ));
    return this.call('/item', [], JSON.stringify(body), 'post', 'item').pipe(map(result => {
      this.activity.emit(new ODBActivity(
        ODBActivityType.create,
        ODBActivityStatus.ended,
        false,
        ''
      ));
      if (result instanceof SNItem) {
        return result;
      }
      this.activity.emit(new ODBActivity(
        ODBActivityType.create,
        ODBActivityStatus.error,
        true,
        'the result is not an SNItem or an Error'
      ));
      throwError('the result is not an SNItem or an Error');
    }));
  }

  itemById(itemid: number, childs?: string[], reload = false): Observable<SNItem> {
    return new Observable(observer => {
      if (!reload && this.itemsById.has(itemid)) {
        observer.next(this.itemsById.get(itemid));
      } else {
        const queryParams = [];
        if (childs) {
          queryParams.push(['childs', childs.join(';')]);
        }
        this.call('/item/id/' + itemid, queryParams, '', 'get', 'item').subscribe(result => {
          if (result instanceof SNItem) {
            // Store this item in cache
            this.itemsById.set(result.id, result);
            this.itemStored.emit(result);
            observer.next(result);
          }
          throwError('the result is not an SNItem or an Error');
        });
      }
    });
  }

  itemById$(itemid: number): SNItem {
    if (this.itemsById.has(itemid)) {
      return this.itemsById.get(itemid);
    } else {
      return null;
    }
  }

  itemByKey(itemkey: string, singleton = false): Observable<SNItem | SNItem[]> {
    const queryparams = this.deepParams(itemkey);
    if (singleton) {
      if (this.itemsingleton.has(itemkey)) {
        return new Observable(observer => {
          observer.next(this.itemsingleton.get(itemkey));
        });
      }
      return this.call('/item/singleton/' + itemkey, queryparams, '', 'get', 'item').pipe(map(result => {
        if (result instanceof SNItem) {
          this.itemsingleton.set(itemkey, result);
          return result;
        }
        throwError('the result is not an SNItem or an Error');
      }));
    } else {
      if (this.itemsByKey.has(itemkey)) {
        return new Observable(observer => {
          observer.next(this.itemsByKey.get(itemkey));
        });
      }
      return this.call('/item/key/' + itemkey, queryparams, '', 'get', 'items').pipe(map(results => {
        if (results instanceof Array) {
          if (!this.itemsByKey.has(itemkey)) {
            this.itemsByKey.set(itemkey, []);
          }
          results.forEach(item => {
            if (item instanceof SNItem) {
              this.itemsById.set(item.id, item);
              this.itemStored.emit(item);
              this.itemsByKey.get(itemkey).push(item);
            }
          });
          return results;
        }
        throwError('the result is not an SNItem or an Error');
      }));
    }
  }

  singleton(itemkey: string): Observable<SNItem> {
    return this.itemByKey(itemkey, true) as Observable<SNItem>;
  }

  addChild(parent: SNItem, child: SNItem): Observable<SNItem> {
    if (child.id !== 0) {
      // Just add this child
      return new Observable(observer => {
        this.call('/item/id/' + parent.id + '/child/id/' + child.id, [], '', 'post', 'child').subscribe(result => {
          console.log('child added', result);
          if (typeof(result.child) !== 'undefined') {
            observer.next(this.factory.itemFromJson(result.child));
          } else {
            console.log('child is not a SNItem');
            observer.error('the result is not an SNItem or an Error');
          }
        });
      });
    }
    // create and add child
    return new Observable(observer => {
      const itemJson = child.toJson();
      this.call('/item/id/' + parent.id + '/child', [], '{"child":' + itemJson + '}', 'post', 'child').subscribe(result => {
        console.log('child added', result);
        if (typeof(result.child) !== 'undefined') {
          const newchild = this.factory.itemFromJson(result.child);
          // Add to cache
          this.itemsById.set(newchild.id, newchild);
          observer.next(newchild);
        } else {
          console.log('child is not a SNItem');
          observer.error('the result is not an SNItem or an Error');
        }
      });
    });
  }

  addParent(child: SNItem, parent: SNItem): Observable<SNItem> {
    if (parent.id !== 0) {
      return new Observable(observer => {
        // Just add this parent
        this.call('/item/id/' + child.id + '/parent/id/' + parent.id, [], '', 'post', 'parent').subscribe(result => {
          console.log('parent added', result);
          if (typeof(result.parent) !== 'undefined') {
            observer.next(this.factory.itemFromJson(result.parent));
          } else {
            console.log('parent is not a SNItem');
            observer.error('the result is not an SNItem or an Error');
          }
        });
      });
    }
    // create and add parent
    const body = {
      parent
    };
    return new Observable(observer => {
      this.call('/item/id/' + child.id + '/parent', [], JSON.stringify(body), 'post', 'parent').subscribe(result => {
        console.log('parent added', result);
        if (typeof(result.parent) !== 'undefined') {
          observer.next(this.factory.itemFromJson(result.parent));
        } else {
          console.log('parent is not a SNItem');
          observer.error('the result is not an SNItem or an Error');
        }
      });
    });
  }

  removeItem(item: SNItem): Observable<SNItem> {
    return new Observable(observer => {
      this.call('/item/id/' + item.id, [], '', 'delete', 'item').subscribe(result => {
        if (typeof(result.item) !== 'undefined') {
          observer.next(this.factory.itemFromJson(result.item));
        } else {
          console.log('parent is not a SNItem');
          observer.error('the result is not an SNItem or an Error');
        }
      });
    });
  }

  removeChild(parent: SNItem, child: SNItem, link?: boolean): Observable<SNItem> {
    if (!!link && link === true) {
      // Remove only link
      return new Observable(observer => {
        this.call('/item/id/' + parent.id + '/child/link/id/' + child.id, [], '', 'delete', 'child').subscribe(result => {
          if (typeof(result.child) !== 'undefined') {
            observer.next(this.factory.itemFromJson(result.child));
          } else {
            observer.error('the result is not an SNItem or an Error');
          }
        });
      });
    }
    return new Observable(observer => {
      this.call('/item/id/' + parent.id + '/child/id/' + child.id, [], '', 'delete', 'child').subscribe(result => {
        if (typeof(result.child) !== 'undefined') {
          observer.next(this.factory.itemFromJson(result.child));
        } else {
          observer.error('the result is not an SNItem or an Error');
        }
      });
    });
  }

  childs<T extends SNItem>(item: SNItem, childsKey?: string, subclasses?: boolean): Observable<Array<T>> {
    return new Observable(observer => {
      const queryparams = this.deepParams(childsKey);
      if (!!subclasses) {
        queryparams.push(['subclasses', 'true']);
      }
      if (!!childsKey) {
//        console.log('calling call');
          this.call('/item/id/' + item.id + '/childs/key/' + encodeURI(childsKey), queryparams, '', 'get', 'childs').subscribe(results => {
//          console.log('return from call');
          if (results instanceof Array) {
            // Store these items in cache
            results.forEach(result => {
              if (!this.itemsById.has(result.id)) {
                this.itemsById.set(result.id, result);
                this.itemStored.emit(item);
              }
            });
            observer.next(results);
          } else {
            observer.next([]);
          }
        }, error => {
          console.log('call to call failed', error);
        }, () => {
          console.log('call to call complete');
        });
      } else {
        this.call('/item/id/' + item.id + '/childs', queryparams, '', 'get', 'childs').subscribe(results => {
          if (results instanceof Array) {
            observer.next(results);
          } else {
            observer.next([]);
          }
        });
      }
    });
  }

  parents(item: SNItem, parentsKey?: string): Observable<SNItem[]> {
    if (!!parentsKey) {
      return this.call('/item/id/' + item.id + '/parents/key/' + encodeURI(parentsKey), [], '', 'get', 'parents').pipe(map(results => {
        if (results instanceof Array) {
          return results;
        }
        return [];
      }));
    }
    return this.call('/item/id/' + item.id + '/parents', [], '', 'get', 'parents').pipe(map(results => {
      if (results instanceof Array) {
        return results;
      }
      return [];
    }));
  }

  update(item: SNItem): Observable<SNItem> {
    this.activity.emit(new ODBActivity(
      ODBActivityType.update,
      ODBActivityStatus.starting,
      false,
      ''
    ));
    return new Observable(observer => {
      this.call('/item/id/' + item.id, [], item.toJson(), 'put', 'item').subscribe(result => {
        this.activity.emit(new ODBActivity(
          ODBActivityType.update,
          ODBActivityStatus.ended,
          false,
          ''
        ));
        if (typeof(result.item) !== 'undefined') {
          observer.next(this.factory.itemFromJson(result.item));
        } else {
          this.activity.emit(new ODBActivity(
            ODBActivityType.update,
            ODBActivityStatus.error,
            true,
            'item is not a SNItem'
          ));
          console.log('item is not a SNItem');
          observer.error('the result is not an SNItem or an Error');
        }
      }, error => {
        this.activity.emit(new ODBActivity(
          ODBActivityType.update,
          ODBActivityStatus.error,
          true,
          error.message
        ));
        observer.error(error);
      });
    });
  }

  itemAccess(item: SNItem): Observable<SNItemAccess[]> {
    return new Observable(observer => {
      this.call('/item/' + item.id.toString() + '/security').subscribe(results => {
        if (results.success === false) {
          observer.error('Unable to get item access');
        } else {
          const resultaccess: SNItemAccess[] = results.access;
          observer.next(resultaccess);
        }
      });
    });
  }

  setItemAccess(item: SNItem, access: SNItemAccess[]): Observable<SNItemAccess[]> {
    return new Observable(observer => {
      this.call('/item/' + item.id.toString() + '/security', [], JSON.stringify(access), 'put').subscribe(results => {
        if (results.success === false) {
          observer.error('Unable to set item access');
        } else {
          const resultaccess: SNItemAccess[] = results.access;
          observer.next(resultaccess);
        }
      });
    });
  }

  itemReload<T extends SNItem>(item: SNItem): Observable<T> {
    return new Observable(observer => {
      this.call('/item/id/' + item.id, [], '', 'get').subscribe(results => {
        item.fromJson(results.item);
      }, error => {
        observer.error('the result is not an SNItem or an Error');
      });
    });
  }

  /******************** HELPERS *********************/
  /**
   *
   * @param rest the rest call
   * @param queryvalues URI query params
   * @param body The call body
   * @param method The method used : get,post,put,delete
   * @param resultkey The keys used to return values as array
   */

  call(rest: string, queryvalues: [string, string][] = [], body: string = '',
       method: string = 'get', resultkey: string = ''): Observable<any> {
    return new Observable(observer => {
      let urltocall = this.callurl + rest;
      if (queryvalues.length !== 0) {
        const urlparams = [];
        queryvalues.forEach(valuepair => {
          urlparams.push(valuepair.join('='));
        });
        urltocall += '?' + encodeURI(urlparams.join('&'));
      }
      switch (method) {
        case 'post':
          {
            const httpOptions = {
              headers: new HttpHeaders({
                'Content-Type':  'application/json',
                Authorization: 'Bearer ' + this.bearer
              }),
              withCredentials: true
            };
            this.http.post(urltocall, body, httpOptions).subscribe(results => {
              observer.next(results);
            }, error => {
              observer.error(error);
            });
          }
          break;
        case 'put':
          {
            const httpOptions = {
              headers: new HttpHeaders({
                'Content-Type':  'application/json',
                Authorization: 'Bearer ' + this.bearer
              }),
              withCredentials: true
            };
            this.http.put(urltocall, body, httpOptions).subscribe(results => {
              try {
                if (resultkey.length !== 0) {
                  if (typeof results[resultkey] === 'undefined') {
                    // No result key found
                    console.log('No result key found ', results);
                    observer.error('No result key found');
                  } else {
                    results = results[resultkey];
                    if (results instanceof Array) {
//                        console.log('Build items from json');
                      const buildItems = this.factory.itemsFromJson(results);
                      buildItems.forEach(buildItem => {
                        this.itemsById.set(buildItem.id, buildItem);
                        this.itemStored.emit(buildItem);
                      });
                      observer.next(buildItems);
                    } else {
//                        console.log('Build item from json');
                      const buildItem = this.factory.itemFromJson(results);
                      this.itemsById.set(buildItem.id, buildItem);
                      this.itemStored.emit(buildItem);
                      observer.next(buildItem);
                    }
                  }
                } else {
                  observer.next(results);
                }
              } catch (exception) {
                // Just return json
                console.log('Exception ' + exception.message + ' during call to ', urltocall);
                observer.error('Exception during call to');
              }
            }, error => {
              observer.error(error);
            });
          }
          break;
        case 'get':
          {
            try {
//              console.log('Calling http get');
              const httpOptions = {
                headers: new HttpHeaders({
                  'Content-Type':  'application/json',
                  Authorization: 'Bearer ' + this.bearer
                }),
                withCredentials: true
              };
              this.http.get(urltocall, httpOptions).subscribe(results => {
//                console.log('http get called');
                try {
                  if (resultkey.length !== 0) {
                    if (typeof results[resultkey] === 'undefined') {
                      // No result key found
                      console.log('No result key found ', results);
                      observer.error('No result key found');
                    } else {
                      results = results[resultkey];
                      if (results instanceof Array) {
//                        console.log('Build items from json');
                        const buildItems = this.factory.itemsFromJson(results);
                        buildItems.forEach(buildItem => {
                          this.itemsById.set(buildItem.id, buildItem);
                          this.itemStored.emit(buildItem);
                        });
                        observer.next(buildItems);
                      } else {
//                        console.log('Build item from json');
                        const buildItem = this.factory.itemFromJson(results);
                        this.itemsById.set(buildItem.id, buildItem);
                        this.itemStored.emit(buildItem);
                        observer.next(buildItem);
                      }
                    }
                  } else {
                    observer.next(results);
                  }
                } catch (exception) {
                  // Just return json
                  console.log('Exception ' + exception.message + ' during call to ', urltocall);
                  observer.error('Exception during call to');
                }
              }, error => {
                observer.error(error);
              });
            } catch {
              console.log('Error while http get');
              observer.error('Error while http get');
            }
          }
          break;
        case 'delete':
          {
            this.http.delete(urltocall).subscribe(results => {
              observer.next(results);
            }, error => {
              observer.error(error);
            });
          }
          break;
        default:
          observer.error('Wrong method' + method);
      }
    });
  }
}
